import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteSvg from "../../../assets/Icons/Delete _ Default.svg";
import { useAlert } from "../../../hooks/useAlert";
import { RequiredAsterisk } from "../../UI/RequiredAsterisk/RequiredAsterisk";
import "./CreateTimeTrial.css";

// Import the mutation hooks
import {
  useCreateTimeTrailMutation,
  useUpdateTimeTrailMutation,
} from "../../../redux-toolkit/features/time-trails/timeTrailsApiSlice";

// Utility function to combine date and time into ISO timestamp
const combineDateAndTime = (date, time) => {
  if (!date || !time) return null;

  // Create a date object from the date string
  const dateObj = new Date(date);

  // Extract hours and minutes from time string (format: "HH:MM")
  const [hours, minutes] = time.split(":").map(Number);

  // Set the time on the date object
  dateObj.setHours(hours, minutes, 0, 0);

  // Return ISO string
  return dateObj.toISOString();
};

// Utility function to get today's date in YYYY-MM-DD format
const getTodayDate = () => {
  const today = new Date();
  return today.toISOString().split("T")[0];
};

// Utility function to split ISO timestamp into date and time
const splitTimestampIntoDateAndTime = (timestamp) => {
  if (!timestamp) return { date: "", time: "" };

  try {
    const dateObj = new Date(timestamp);

    // Format date as YYYY-MM-DD
    const date = dateObj.toISOString().split("T")[0];

    // Format time as HH:MM
    const hours = dateObj.getHours().toString().padStart(2, "0");
    const minutes = dateObj.getMinutes().toString().padStart(2, "0");
    const time = `${hours}:${minutes}`;

    return { date, time };
  } catch (error) {
    console.error("Error parsing timestamp:", error);
    return { date: "", time: "" };
  }
};

// Utility function to validate date range
const validateDateRange = (
  startDate,
  startTime,
  endDate,
  endTime,
  isEditMode = false,
) => {
  if (!startDate || !startTime || !endDate || !endTime) {
    return {
      isValid: false,
      message: "Please fill in all date and time fields",
    };
  }

  const startTimestamp = combineDateAndTime(startDate, startTime);
  const endTimestamp = combineDateAndTime(endDate, endTime);

  if (!startTimestamp || !endTimestamp) {
    return { isValid: false, message: "Invalid date or time format" };
  }

  const start = new Date(startTimestamp);
  const end = new Date(endTimestamp);
  const now = new Date();

  // Check if start date is in the past


  // For edit mode, allow past start dates if the time trial is already scheduled
  if (!isEditMode && start < now) {
    return {
      isValid: false,
      message: "Start date and time cannot be in the past",
    };
  }

  // Check if end date is before start date
  if (end <= start) {
    return {
      isValid: false,
      message: "End date and time must be after start date and time",
    };
  }

  return { isValid: true, message: "" };
};

export const TimeTrialModal = ({
  timeTrialOpen,
  setTimeTrialOpen,
  editData = null,
  isEditMode = false,
  isPreviewMode = false,
  selectedCompany,
  refetch = null,
}) => {
  const getPointsValidationError = (value) => {
    const pointsValue = String(value ?? "").trim();

    if (!pointsValue) return "";
    if (pointsValue.includes(".")) return "input cannot be in decimal";
    if (pointsValue.startsWith("-") || Number(pointsValue) < 0) {
      return "Points cannot be a negative value";
    }
    if (/^\d+$/.test(pointsValue) && Number(pointsValue) >= 9999) {
      return "Points must be less than 9999";
    }
    return "";
  };

  const [formData, setFormData] = useState({
    task: "",
    points: "",
    startDate: "",
    startTime: "",
    endDate: "",
    endTime: "",
  });
  const [errors, setErrors] = useState({});

  // Use the mutation hooks
  const [createTimeTrial, { isLoading: isCreating }] =
    useCreateTimeTrailMutation();
  const [updateTimeTrial, { isLoading: isUpdating }] =
    useUpdateTimeTrailMutation();

  const isLoading = isCreating || isUpdating;

  const { displayAlert } = useAlert();


  const handleClose = () => {
    setTimeTrialOpen(false);
    // Reset form when closing
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      task: "",
      points: "",
      startDate: "",
      startTime: "",
      endDate: "",
      endTime: "",
    });
    setErrors({});
  };

  const handleInputChange = (field, value) => {
    if (field === "points") {
      // Permit numeric-like input for inline validation feedback.
      if (!/^-?\d*\.?\d*$/.test(value)) {
        return;
      }

      const pointsError = getPointsValidationError(value);
      setFormData((prev) => ({
        ...prev,
        points: value,
      }));
      setErrors((prev) => ({
        ...prev,
        points: pointsError,
      }));
      return;
    }

    setFormData((prev) => {
      const newData = {
        ...prev,
        [field]: value,
      };

      // If start date is changed and end date is before the new start date, clear end date
      if (field === "startDate" && newData.endDate && newData.endDate < value) {
        newData.endDate = "";
        newData.endTime = "";
      }

      return newData;
    });
  };

  const handleSubmit = async () => {
    try {
      // Clear previous errors
      setErrors({});

      // Validate required fields
      const newErrors = {};

      if (!formData.task) {
        newErrors.task = "Task is required";
      }
      if (!formData.points) {
        newErrors.points = "Points is required";
      }
      if (!formData.startDate) {
        newErrors.startDate = "Start date is required";
      }
      if (!formData.startTime) {
        newErrors.startTime = "Start time is required";
      }
      if (!formData.endDate) {
        newErrors.endDate = "End date is required";
      }
      if (!formData.endTime) {
        newErrors.endTime = "End time is required";
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        displayAlert({
          open: true,
          message: "Please fill in all required fields including date and time",
          type: "error",
          timeout: "3000",
        });
        return;
      }

      const pointsStr = String(formData.points ?? "").trim();
      const pointsNum = Number(pointsStr);
      const pointsValidationError = getPointsValidationError(pointsStr);

      if (pointsValidationError) {
        setErrors((prev) => ({
          ...prev,
          points: pointsValidationError,
        }));
        displayAlert({
          open: true,
          message: pointsValidationError,
          type: "error",
          timeout: "3000",
        });
        return;
      }

      if (!/^\d+$/.test(pointsStr)) {
        setErrors((prev) => ({
          ...prev,
          points: "Points must be a whole number",
        }));
        displayAlert({
          open: true,
          message: "Points must be a whole number",
          type: "error",
          timeout: "3000",
        });
        return;
      }

      if (pointsNum === 0) {
        setErrors((prev) => ({
          ...prev,
          points: "Points cannot be zero",
        }));
        displayAlert({
          open: true,
          message: "Points cannot be zero",
          type: "error",
          timeout: "3000",
        });
        return;
      }

      // Validate date range
      const dateValidation = validateDateRange(
        formData.startDate,
        formData.startTime,
        formData.endDate,
        formData.endTime,
        isEditMode,
      );

      if (!dateValidation.isValid) {
        displayAlert({
          open: true,
          message: dateValidation.message,
          type: "error",
          timeout: "3000",
        });
        return;
      }

      // Combine date and time into timestamps
      const startTimestamp = combineDateAndTime(
        formData.startDate,
        formData.startTime,
      );
      const endTimestamp = combineDateAndTime(
        formData.endDate,
        formData.endTime,
      );

      // Prepare the time trial data with combined timestamps
      const timeTrialData = {
        task: formData.task,
        points: pointsNum,
        startDate: startTimestamp,
        endDate: endTimestamp,
      };


      if (isEditMode && editData) {
        // Update existing time trial
        const updatePayload = {
          id: editData._id,
          data: timeTrialData,
        };
        await updateTimeTrial(updatePayload).unwrap();
        displayAlert({
          open: true,
          message: "Time trial updated successfully!",
          type: "success",
          timeout: "3000",
        });
        // Refetch data to update the UI
        if (refetch) {
          refetch();
        }
      } else {
        // Create new time trial
        await createTimeTrial({
          createTimeTrailBody: timeTrialData,
        }).unwrap();
        displayAlert({
          open: true,
          message: "Time trial created successfully!",
          type: "success",
          timeout: "3000",
        });
      }

      handleClose();
    } catch (error) {
      console.error("Error saving time trial:", error);
      displayAlert({
        open: true,
        message:
          error?.data?.message ||
          `Failed to ${
            isEditMode ? "update" : "create"
          } time trial. Please try again.`,
        type: "error",
        timeout: "3000",
      });
    }
  };

  const handleDelete = () => {
    resetForm();
  };

  // Generate time options for dropdowns
  const generateTimeOptions = () => {
    const times = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeString = `${hour.toString().padStart(2, "0")}:${minute
          .toString()
          .padStart(2, "0")}`;
        times.push(timeString);
      }
    }
    return times;
  };

  const timeOptions = generateTimeOptions();

  // Populate form data when in edit mode
  React.useEffect(() => {
    if (timeTrialOpen && isEditMode && editData) {
      // Split timestamps into date and time components
      const startDateTime = splitTimestampIntoDateAndTime(editData.startDate);
      const endDateTime = splitTimestampIntoDateAndTime(editData.endDate);

      setFormData({
        task: editData.task || "",
        points: editData.points?.toString() || "",
        startDate: startDateTime.date,
        startTime: startDateTime.time,
        endDate: endDateTime.date,
        endTime: endDateTime.time,
      });
    }
  }, [timeTrialOpen, isEditMode, editData]);

  return (
    <Dialog
      open={timeTrialOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth={false}
      PaperProps={{
        sx: {
          borderRadius: "12px",
          // minHeight: "500px",
          width: "750px",
          maxWidth: "750px",
        },
      }}
    >
      <DialogTitle
        sx={{
          mx: 6,
          py: 3,
          px: 0,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <span className="time_trial_title">
          {isPreviewMode
            ? "Preview Time Trial"
            : isEditMode
              ? "Edit Time Trial"
              : "Add Time Trial"}
        </span>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            color: "#444444",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "90%",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto",
          padding: "20px",
          marginTop: "30px",
          paddingBottom: "0px",
        }}
      >
        {/* Task Field */}
        <div className="form-field">
          <label className="form-label">
            Task <RequiredAsterisk />
          </label>
          <input
            type="text"
            placeholder="Eg: Question"
            value={formData.task}
            onChange={(e) => handleInputChange("task", e.target.value)}
            className={`form-input ${errors.task ? "field_error" : ""}`}
            readOnly={isPreviewMode}
          />
        </div>

        {/* Points Field */}
        <div className="form-field">
          <label className="form-label">
            Points <RequiredAsterisk />
          </label>
          <input
            type="text"
            value={formData.points}
            onChange={(e) => handleInputChange("points", e.target.value)}
            className={`form-input ${errors.points ? "field_error" : ""}`}
            readOnly={isPreviewMode}
            inputMode="numeric"
          />
          {errors.points ? (
            <p className="form-input-error">{errors.points}</p>
          ) : null}
        </div>

        {/* Start Time Section */}
        <div className="form-field">
          <label className="form-label">
            Start Time <RequiredAsterisk />
          </label>
          <div className="form-row">
            <div
              style={{
                width: "65%",
                minWidth: "65%",
              }}
              className="form-field"
            >
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => handleInputChange("startDate", e.target.value)}
                className={`form-input ${
                  errors.startDate ? "field_error" : ""
                }`}
                min={getTodayDate()}
                readOnly={isPreviewMode}
              />
            </div>
            <div className="form-field">
              <select
                value={formData.startTime}
                onChange={(e) => handleInputChange("startTime", e.target.value)}
                className={`form-input ${
                  errors.startTime ? "field_error" : ""
                }`}
                disabled={isPreviewMode}
              >
                <option value=""> Time</option>
                {timeOptions.map((time) => (
                  <option key={time} value={time}>
                    {time}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* End Time Section */}
        <div className="form-field">
          <label className="form-label">
            End Time <RequiredAsterisk />
          </label>
          <div className="form-row">
            <div
              style={{
                width: "65%",
                minWidth: "65%",
              }}
              className="form-field"
            >
              <input
                type="date"
                value={formData.endDate}
                onChange={(e) => handleInputChange("endDate", e.target.value)}
                className={`form-input ${errors.endDate ? "field_error" : ""}`}
                min={formData.startDate || getTodayDate()}
                readOnly={isPreviewMode}
              />
            </div>
            <div className="form-field">
              <select
                value={formData.endTime}
                onChange={(e) => handleInputChange("endTime", e.target.value)}
                className={`form-input ${errors.endTime ? "field_error" : ""}`}
                disabled={isPreviewMode}
              >
                <option value="">Time</option>
                {timeOptions.map((time) => (
                  <option key={time} value={time}>
                    {time}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </DialogContent>

      <DialogActions
        sx={{
          pt: 2,
          pb: 4.5,
          px: 8,
          justifyContent: "flex-end",
          gap: 2,
        }}
      >
        {!isPreviewMode && (
          <IconButton
            onClick={handleDelete}
            sx={{
              color: "gray",
              "&:hover": {
                color: "#ff0000",
              },
            }}
          >
            <img
              src={DeleteSvg}
              alt="Delete"
              style={{ width: "21px", height: "21px" }}
            />
          </IconButton>
        )}

        <Button
          variant="contained"
          onClick={isPreviewMode ? handleClose : handleSubmit}
          disabled={isLoading}
          sx={{
            backgroundColor: "#3361cf",
            minWidth: "120px",
            padding: "10px 24px",
            fontSize: "16px",
            fontWeight: "500",
            lineHeight: "normal",
            fontFamily: "Montserrat",
            textTransform: "capitalize",
            color: "#FFF",

            "&:hover": {
              backgroundColor: "#2a4f9e",
            },
          }}
        >
          {isPreviewMode
            ? "Close"
            : isLoading
              ? isEditMode
                ? "Updating..."
                : "Creating..."
              : isEditMode
                ? "Update"
                : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
